/**
 * SecurityCheckpoint - Security scanning station for delegates and vehicles
 */

import React, { useState, useRef, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogTrigger } from '../components/ui/dialog';
import { toast } from 'sonner';
import {
  ScanLine, Car, Users, LogIn, LogOut, Search, CheckCircle2,
  XCircle, Clock, AlertTriangle, Loader2, Plus, RefreshCw,
  Shield, MapPin, User, Trash2
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SecurityCheckpoint = ({ eventId }) => {
  const queryClient = useQueryClient();
  const [scanInput, setScanInput] = useState('');
  const [vehiclePlate, setVehiclePlate] = useState('');
  const [checkpoint, setCheckpoint] = useState('main_gate');
  const [scanType, setScanType] = useState('entry');
  const [lastScanResult, setLastScanResult] = useState(null);
  const [showRegisterVehicle, setShowRegisterVehicle] = useState(false);
  const [lookupQuery, setLookupQuery] = useState('');
  const [lookupResult, setLookupResult] = useState(null);
  const scanInputRef = useRef(null);
  
  const [vehicleForm, setVehicleForm] = useState({
    plate_number: '',
    delegate_id: '',
    make: '',
    model: '',
    color: '',
    vehicle_type: 'car'
  });

  // Get token helper
  const getToken = () => {
    try {
      const authStorage = localStorage.getItem('eventflow-auth');
      if (authStorage) {
        const parsed = JSON.parse(authStorage);
        return parsed?.state?.token || localStorage.getItem('eventflow_token');
      }
    } catch (e) {}
    return localStorage.getItem('eventflow_token');
  };

  // Auto-focus scan input
  useEffect(() => {
    if (scanInputRef.current) {
      scanInputRef.current.focus();
    }
  }, [lastScanResult]);

  // Fetch security stats
  const { data: logsData, refetch: refetchLogs } = useQuery({
    queryKey: ['security-logs', eventId],
    queryFn: async () => {
      const res = await fetch(`${API_URL}/api/events/${eventId}/security/logs?limit=20`, {
        headers: { 'Authorization': `Bearer ${getToken()}` }
      });
      if (!res.ok) throw new Error('Failed to fetch logs');
      return res.json();
    },
    refetchInterval: 30000 // Refresh every 30 seconds
  });

  // Fetch on-premises count
  const { data: onPremisesData, refetch: refetchOnPremises } = useQuery({
    queryKey: ['on-premises', eventId],
    queryFn: async () => {
      const res = await fetch(`${API_URL}/api/events/${eventId}/security/on-premises`, {
        headers: { 'Authorization': `Bearer ${getToken()}` }
      });
      if (!res.ok) throw new Error('Failed to fetch on-premises');
      return res.json();
    },
    refetchInterval: 30000
  });

  // Fetch vehicles
  const { data: vehiclesData, refetch: refetchVehicles } = useQuery({
    queryKey: ['vehicles', eventId],
    queryFn: async () => {
      const res = await fetch(`${API_URL}/api/events/${eventId}/vehicles`, {
        headers: { 'Authorization': `Bearer ${getToken()}` }
      });
      if (!res.ok) throw new Error('Failed to fetch vehicles');
      return res.json();
    }
  });

  // Security scan mutation
  const scanMutation = useMutation({
    mutationFn: async (data) => {
      const res = await fetch(`${API_URL}/api/events/${eventId}/security/scan`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${getToken()}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(data)
      });
      return res.json();
    },
    onSuccess: (data) => {
      setLastScanResult(data);
      if (data.success) {
        toast.success(
          <div>
            <p className="font-semibold">{data.scan_type === 'entry' ? 'Entry Recorded' : 'Exit Recorded'}</p>
            <p>{data.delegate?.name}</p>
            {data.vehicle && <p className="text-sm">Vehicle: {data.vehicle.plate}</p>}
          </div>
        );
        refetchLogs();
        refetchOnPremises();
      } else {
        toast.error(data.message || 'Scan failed');
      }
      setScanInput('');
      setVehiclePlate('');
    },
    onError: (err) => {
      toast.error(err.message || 'Scan failed');
      setLastScanResult({ success: false, error: err.message });
    }
  });

  // Register vehicle mutation
  const registerVehicleMutation = useMutation({
    mutationFn: async (data) => {
      const res = await fetch(`${API_URL}/api/events/${eventId}/vehicles`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${getToken()}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(data)
      });
      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.detail || 'Failed to register vehicle');
      }
      return res.json();
    },
    onSuccess: () => {
      toast.success('Vehicle registered successfully!');
      queryClient.invalidateQueries(['vehicles', eventId]);
      setShowRegisterVehicle(false);
      setVehicleForm({
        plate_number: '',
        delegate_id: '',
        make: '',
        model: '',
        color: '',
        vehicle_type: 'car'
      });
    },
    onError: (err) => toast.error(err.message)
  });

  // Lookup mutation
  const lookupMutation = useMutation({
    mutationFn: async (identifier) => {
      const res = await fetch(`${API_URL}/api/events/${eventId}/security/lookup/${encodeURIComponent(identifier)}`, {
        headers: { 'Authorization': `Bearer ${getToken()}` }
      });
      return res.json();
    },
    onSuccess: (data) => {
      setLookupResult(data);
      if (!data.success) {
        toast.error(data.message || 'Not found');
      }
    }
  });

  // Delete vehicle mutation
  const deleteVehicleMutation = useMutation({
    mutationFn: async (vehicleId) => {
      const res = await fetch(`${API_URL}/api/events/${eventId}/vehicles/${vehicleId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${getToken()}` }
      });
      if (!res.ok) throw new Error('Failed to delete vehicle');
      return res.json();
    },
    onSuccess: () => {
      toast.success('Vehicle removed');
      queryClient.invalidateQueries(['vehicles', eventId]);
    }
  });

  const handleScan = () => {
    if (!scanInput.trim()) {
      toast.error('Please scan or enter a QR code');
      return;
    }
    
    scanMutation.mutate({
      qr_data: scanInput.trim(),
      checkpoint,
      scan_type: scanType,
      vehicle_plate: vehiclePlate.trim() || null
    });
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleScan();
    }
  };

  const stats = logsData?.stats || {};
  const logs = logsData?.logs || [];
  const vehicles = vehiclesData?.vehicles || [];

  const checkpoints = [
    { id: 'main_gate', name: 'Main Gate' },
    { id: 'parking_entrance', name: 'Parking Entrance' },
    { id: 'vip_entrance', name: 'VIP Entrance' },
    { id: 'service_entrance', name: 'Service Entrance' }
  ];

  return (
    <div className="space-y-6">
      {/* Stats Overview */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">On Premises</p>
                <p className="text-2xl font-bold text-green-600">{onPremisesData?.delegate_count || 0}</p>
              </div>
              <Users className="h-8 w-8 text-green-500" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Vehicles On-site</p>
                <p className="text-2xl font-bold text-blue-600">{onPremisesData?.vehicle_count || 0}</p>
              </div>
              <Car className="h-8 w-8 text-blue-500" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Today's Entries</p>
                <p className="text-2xl font-bold">{stats.today_entries || 0}</p>
              </div>
              <LogIn className="h-8 w-8 text-green-500" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Today's Exits</p>
                <p className="text-2xl font-bold">{stats.today_exits || 0}</p>
              </div>
              <LogOut className="h-8 w-8 text-orange-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      <Tabs defaultValue="scan" className="space-y-4">
        <TabsList>
          <TabsTrigger value="scan">
            <ScanLine className="h-4 w-4 mr-2" /> Scan Station
          </TabsTrigger>
          <TabsTrigger value="vehicles">
            <Car className="h-4 w-4 mr-2" /> Vehicles ({vehicles.length})
          </TabsTrigger>
          <TabsTrigger value="logs">
            <Clock className="h-4 w-4 mr-2" /> Activity Log
          </TabsTrigger>
          <TabsTrigger value="lookup">
            <Search className="h-4 w-4 mr-2" /> Lookup
          </TabsTrigger>
        </TabsList>

        {/* Scan Station Tab */}
        <TabsContent value="scan">
          <div className="grid md:grid-cols-2 gap-6">
            {/* Scan Input */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Shield className="h-5 w-5" />
                  Security Scan
                </CardTitle>
                <CardDescription>Scan delegate QR code for entry/exit</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Checkpoint</Label>
                    <Select value={checkpoint} onValueChange={setCheckpoint}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {checkpoints.map(cp => (
                          <SelectItem key={cp.id} value={cp.id}>{cp.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Scan Type</Label>
                    <div className="flex gap-2">
                      <Button
                        variant={scanType === 'entry' ? 'default' : 'outline'}
                        className={scanType === 'entry' ? 'bg-green-600 hover:bg-green-700 flex-1' : 'flex-1'}
                        onClick={() => setScanType('entry')}
                      >
                        <LogIn className="h-4 w-4 mr-1" /> Entry
                      </Button>
                      <Button
                        variant={scanType === 'exit' ? 'default' : 'outline'}
                        className={scanType === 'exit' ? 'bg-orange-600 hover:bg-orange-700 flex-1' : 'flex-1'}
                        onClick={() => setScanType('exit')}
                      >
                        <LogOut className="h-4 w-4 mr-1" /> Exit
                      </Button>
                    </div>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>QR Code / Badge Code</Label>
                  <Input
                    ref={scanInputRef}
                    value={scanInput}
                    onChange={(e) => setScanInput(e.target.value)}
                    onKeyPress={handleKeyPress}
                    placeholder="Scan QR code or enter badge code..."
                    className="text-lg h-12"
                    data-testid="security-scan-input"
                    autoFocus
                  />
                </div>

                <div className="space-y-2">
                  <Label>Vehicle Plate (optional)</Label>
                  <Input
                    value={vehiclePlate}
                    onChange={(e) => setVehiclePlate(e.target.value.toUpperCase())}
                    placeholder="ABC123"
                    className="uppercase"
                  />
                </div>

                <Button 
                  onClick={handleScan}
                  disabled={scanMutation.isPending || !scanInput.trim()}
                  className={`w-full h-12 text-lg ${scanType === 'entry' ? 'bg-green-600 hover:bg-green-700' : 'bg-orange-600 hover:bg-orange-700'}`}
                  data-testid="security-scan-btn"
                >
                  {scanMutation.isPending ? (
                    <Loader2 className="h-5 w-5 animate-spin mr-2" />
                  ) : scanType === 'entry' ? (
                    <LogIn className="h-5 w-5 mr-2" />
                  ) : (
                    <LogOut className="h-5 w-5 mr-2" />
                  )}
                  Record {scanType === 'entry' ? 'Entry' : 'Exit'}
                </Button>
              </CardContent>
            </Card>

            {/* Last Scan Result */}
            <Card>
              <CardHeader>
                <CardTitle>Last Scan Result</CardTitle>
              </CardHeader>
              <CardContent>
                {lastScanResult ? (
                  lastScanResult.success ? (
                    <div className={`p-4 rounded-lg ${lastScanResult.scan_type === 'entry' ? 'bg-green-50 border border-green-200' : 'bg-orange-50 border border-orange-200'}`}>
                      <div className="flex items-center gap-3 mb-3">
                        <CheckCircle2 className={`h-8 w-8 ${lastScanResult.scan_type === 'entry' ? 'text-green-500' : 'text-orange-500'}`} />
                        <div>
                          <p className="font-semibold text-lg">{lastScanResult.delegate?.name}</p>
                          <Badge variant="outline">{lastScanResult.scan_type === 'entry' ? 'ENTRY' : 'EXIT'}</Badge>
                        </div>
                      </div>
                      <div className="space-y-2 text-sm">
                        <div className="flex items-center gap-2">
                          <User className="h-4 w-4 text-muted-foreground" />
                          <span>Badge: {lastScanResult.delegate?.badge_code}</span>
                        </div>
                        {lastScanResult.delegate?.tier && (
                          <div className="flex items-center gap-2">
                            <Shield className="h-4 w-4 text-muted-foreground" />
                            <span>Tier: {lastScanResult.delegate?.tier}</span>
                          </div>
                        )}
                        <div className="flex items-center gap-2">
                          <MapPin className="h-4 w-4 text-muted-foreground" />
                          <span>Checkpoint: {lastScanResult.checkpoint}</span>
                        </div>
                        {lastScanResult.vehicle && (
                          <div className="flex items-center gap-2">
                            <Car className="h-4 w-4 text-muted-foreground" />
                            <span>Vehicle: {lastScanResult.vehicle.plate} ({lastScanResult.vehicle.color} {lastScanResult.vehicle.make} {lastScanResult.vehicle.model})</span>
                          </div>
                        )}
                        <div className="flex items-center gap-2">
                          <Clock className="h-4 w-4 text-muted-foreground" />
                          <span>{new Date(lastScanResult.timestamp).toLocaleString()}</span>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="p-4 rounded-lg bg-red-50 border border-red-200">
                      <div className="flex items-center gap-3">
                        <XCircle className="h-8 w-8 text-red-500" />
                        <div>
                          <p className="font-semibold text-red-800">Scan Failed</p>
                          <p className="text-red-600">{lastScanResult.message || lastScanResult.error}</p>
                        </div>
                      </div>
                    </div>
                  )
                ) : (
                  <div className="text-center py-12 text-muted-foreground">
                    <ScanLine className="h-12 w-12 mx-auto mb-4 opacity-50" />
                    <p>Scan a QR code to see results</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Vehicles Tab */}
        <TabsContent value="vehicles">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Registered Vehicles</CardTitle>
                  <CardDescription>Vehicles registered for this event</CardDescription>
                </div>
                <Dialog open={showRegisterVehicle} onOpenChange={setShowRegisterVehicle}>
                  <DialogTrigger asChild>
                    <Button data-testid="register-vehicle-btn">
                      <Plus className="h-4 w-4 mr-2" /> Register Vehicle
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>Register Vehicle</DialogTitle>
                      <DialogDescription>Link a vehicle to a delegate</DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                      <div className="space-y-2">
                        <Label>Plate Number *</Label>
                        <Input
                          value={vehicleForm.plate_number}
                          onChange={(e) => setVehicleForm({ ...vehicleForm, plate_number: e.target.value.toUpperCase() })}
                          placeholder="ABC 123"
                          className="uppercase"
                          data-testid="vehicle-plate-input"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Delegate ID / Badge Code *</Label>
                        <Input
                          value={vehicleForm.delegate_id}
                          onChange={(e) => setVehicleForm({ ...vehicleForm, delegate_id: e.target.value })}
                          placeholder="DLG-XXXXXXXX or delegate ID"
                          data-testid="vehicle-delegate-input"
                        />
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>Make</Label>
                          <Input
                            value={vehicleForm.make}
                            onChange={(e) => setVehicleForm({ ...vehicleForm, make: e.target.value })}
                            placeholder="Toyota"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Model</Label>
                          <Input
                            value={vehicleForm.model}
                            onChange={(e) => setVehicleForm({ ...vehicleForm, model: e.target.value })}
                            placeholder="Camry"
                          />
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>Color</Label>
                          <Input
                            value={vehicleForm.color}
                            onChange={(e) => setVehicleForm({ ...vehicleForm, color: e.target.value })}
                            placeholder="Silver"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Type</Label>
                          <Select
                            value={vehicleForm.vehicle_type}
                            onValueChange={(value) => setVehicleForm({ ...vehicleForm, vehicle_type: value })}
                          >
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="car">Car</SelectItem>
                              <SelectItem value="motorcycle">Motorcycle</SelectItem>
                              <SelectItem value="truck">Truck</SelectItem>
                              <SelectItem value="bus">Bus</SelectItem>
                              <SelectItem value="van">Van</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                    </div>
                    <DialogFooter>
                      <Button variant="outline" onClick={() => setShowRegisterVehicle(false)}>Cancel</Button>
                      <Button
                        onClick={() => registerVehicleMutation.mutate(vehicleForm)}
                        disabled={registerVehicleMutation.isPending || !vehicleForm.plate_number || !vehicleForm.delegate_id}
                        data-testid="submit-vehicle-btn"
                      >
                        {registerVehicleMutation.isPending && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
                        Register Vehicle
                      </Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
              </div>
            </CardHeader>
            <CardContent>
              {vehicles.length === 0 ? (
                <div className="text-center py-12 text-muted-foreground">
                  <Car className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>No vehicles registered</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {vehicles.map((vehicle) => (
                    <div key={vehicle.id} className="flex items-center justify-between p-4 border rounded-lg">
                      <div className="flex items-center gap-4">
                        <Car className={`h-8 w-8 ${vehicle.is_on_premises ? 'text-green-500' : 'text-gray-400'}`} />
                        <div>
                          <p className="font-semibold text-lg">{vehicle.plate_number}</p>
                          <p className="text-sm text-muted-foreground">
                            {[vehicle.color, vehicle.make, vehicle.model].filter(Boolean).join(' ')}
                          </p>
                          <p className="text-sm">Driver: {vehicle.delegate_name}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <Badge variant={vehicle.is_on_premises ? 'default' : 'outline'}>
                          {vehicle.is_on_premises ? 'On Premises' : 'Off-site'}
                        </Badge>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-red-500 hover:text-red-700"
                          onClick={() => {
                            if (window.confirm('Remove this vehicle registration?')) {
                              deleteVehicleMutation.mutate(vehicle.id);
                            }
                          }}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Activity Log Tab */}
        <TabsContent value="logs">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Activity Log</CardTitle>
                  <CardDescription>Recent security checkpoint activity</CardDescription>
                </div>
                <Button variant="outline" onClick={() => refetchLogs()}>
                  <RefreshCw className="h-4 w-4 mr-2" /> Refresh
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {logs.length === 0 ? (
                <div className="text-center py-12 text-muted-foreground">
                  <Clock className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>No activity yet</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {logs.map((log) => (
                    <div key={log.id} className="flex items-center justify-between p-3 border rounded-lg">
                      <div className="flex items-center gap-3">
                        {log.scan_type === 'entry' ? (
                          <LogIn className="h-5 w-5 text-green-500" />
                        ) : (
                          <LogOut className="h-5 w-5 text-orange-500" />
                        )}
                        <div>
                          <p className="font-medium">{log.delegate_name}</p>
                          <p className="text-sm text-muted-foreground">
                            {log.checkpoint} • {log.delegate_badge}
                            {log.vehicle_plate && ` • Vehicle: ${log.vehicle_plate}`}
                          </p>
                        </div>
                      </div>
                      <div className="text-right text-sm text-muted-foreground">
                        {new Date(log.timestamp).toLocaleTimeString()}
                        <br />
                        {new Date(log.timestamp).toLocaleDateString()}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Lookup Tab */}
        <TabsContent value="lookup">
          <Card>
            <CardHeader>
              <CardTitle>Quick Lookup</CardTitle>
              <CardDescription>Search by badge code, QR code, or vehicle plate</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex gap-2">
                <Input
                  value={lookupQuery}
                  onChange={(e) => setLookupQuery(e.target.value)}
                  placeholder="Enter badge code, delegate ID, or vehicle plate..."
                  className="flex-1"
                  onKeyPress={(e) => e.key === 'Enter' && lookupMutation.mutate(lookupQuery)}
                  data-testid="security-lookup-input"
                />
                <Button
                  onClick={() => lookupMutation.mutate(lookupQuery)}
                  disabled={lookupMutation.isPending || !lookupQuery.trim()}
                >
                  {lookupMutation.isPending ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Search className="h-4 w-4" />
                  )}
                </Button>
              </div>

              {lookupResult && lookupResult.success && (
                <div className="p-4 border rounded-lg space-y-4">
                  {lookupResult.delegate && (
                    <div>
                      <h4 className="font-semibold flex items-center gap-2 mb-2">
                        <User className="h-4 w-4" /> Delegate
                      </h4>
                      <div className="grid grid-cols-2 gap-2 text-sm">
                        <div><span className="text-muted-foreground">Name:</span> {lookupResult.delegate.name}</div>
                        <div><span className="text-muted-foreground">Badge:</span> {lookupResult.delegate.badge_code}</div>
                        <div><span className="text-muted-foreground">Email:</span> {lookupResult.delegate.email || '-'}</div>
                        <div><span className="text-muted-foreground">Phone:</span> {lookupResult.delegate.phone || '-'}</div>
                        <div><span className="text-muted-foreground">Tier:</span> {lookupResult.delegate.tier || '-'}</div>
                        <div>
                          <span className="text-muted-foreground">Status:</span>{' '}
                          <Badge variant={lookupResult.delegate.is_on_premises ? 'default' : 'outline'}>
                            {lookupResult.delegate.is_on_premises ? 'On Premises' : 'Off-site'}
                          </Badge>
                        </div>
                      </div>
                    </div>
                  )}

                  {lookupResult.vehicles?.length > 0 && (
                    <div>
                      <h4 className="font-semibold flex items-center gap-2 mb-2">
                        <Car className="h-4 w-4" /> Vehicles
                      </h4>
                      <div className="space-y-2">
                        {lookupResult.vehicles.map((v) => (
                          <div key={v.id} className="flex items-center justify-between p-2 bg-muted/50 rounded">
                            <span>{v.plate_number} - {v.color} {v.make} {v.model}</span>
                            <Badge variant={v.is_on_premises ? 'default' : 'outline'}>
                              {v.is_on_premises ? 'On Premises' : 'Off-site'}
                            </Badge>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {lookupResult.recent_logs?.length > 0 && (
                    <div>
                      <h4 className="font-semibold flex items-center gap-2 mb-2">
                        <Clock className="h-4 w-4" /> Recent Activity
                      </h4>
                      <div className="space-y-1 text-sm">
                        {lookupResult.recent_logs.slice(0, 5).map((log) => (
                          <div key={log.id} className="flex items-center gap-2">
                            {log.scan_type === 'entry' ? (
                              <LogIn className="h-3 w-3 text-green-500" />
                            ) : (
                              <LogOut className="h-3 w-3 text-orange-500" />
                            )}
                            <span>{log.scan_type} at {log.checkpoint}</span>
                            <span className="text-muted-foreground">
                              {new Date(log.timestamp).toLocaleString()}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default SecurityCheckpoint;
